/**
 * 钉钉免登调试页（无需鉴权）
 *
 * 用途：在钉钉容器内打开本页，获取免登授权码 code，
 * 并调用后端 /api/v1/dashboard/dingtalk/login 验证管理员标识返回。
 *
 * 使用步骤：
 * 1. 填入企业 CorpId（ding 开头，自动记忆到 localStorage）
 * 2. 点击「获取免登码」（必须在钉钉客户端内打开才能成功）
 * 3. 点击「调用登录接口」查看返回的 userId / name / isAdmin
 *
 * 注意：code 5 分钟有效且仅可使用一次，失败后需重新获取。
 */
import { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '@/config/env';

// 钉钉 JSAPI CDN 地址（避免引入 npm 依赖，调试页动态加载）
const DINGTALK_JSAPI_URL = 'https://g.alicdn.com/dingding/dingtalk-jsapi/3.0.25/dingtalk.open.js';

const CORP_ID_STORAGE_KEY = 'dingtalk_test_corp_id';

// 对齐 chat.ts 的接口地址约定：开发环境 API_BASE_URL 为空走相对路径，
// 由 Vite 代理 /analysis → analysis-service:8813（网关未配置 analysis 路由）
const DEFAULT_LOGIN_URL = `${API_BASE_URL}/analysis/api/v1/dashboard/dingtalk/login`;

/** 全局 dd 对象类型兜底声明 */
declare global {
  interface Window {
    dd?: any;
  }
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 640, margin: '0 auto', padding: 16, fontFamily: 'sans-serif' },
  title: { fontSize: 18, fontWeight: 600, margin: '12px 0' },
  label: { display: 'block', fontSize: 13, color: '#666', margin: '12px 0 4px' },
  input: { width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' },
  button: { marginTop: 12, marginRight: 8, padding: '8px 16px', fontSize: 14, border: 'none', borderRadius: 6, background: '#1677ff', color: '#fff', cursor: 'pointer' },
  buttonDisabled: { background: '#9dc4ff', cursor: 'not-allowed' },
  codeBox: { marginTop: 8, padding: 10, background: '#f5f5f5', borderRadius: 6, fontSize: 13, wordBreak: 'break-all' },
  resultBox: { marginTop: 8, padding: 10, background: '#0b1021', color: '#7ee787', borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' },
  tip: { fontSize: 12, color: '#999', marginTop: 8 },
  error: { color: '#d4380d' },
};

export default function DingTalkTestPage() {
  const [corpId, setCorpId] = useState(() => localStorage.getItem(CORP_ID_STORAGE_KEY) || 'dingbb80c4f55ac514bf35c2f4657eb6378f');
  const [loginUrl, setLoginUrl] = useState(DEFAULT_LOGIN_URL);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [inDingTalk, setInDingTalk] = useState(false);
  const [copied, setCopied] = useState(false);
  const jsapiReadyRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    setInDingTalk(/DingTalk/i.test(navigator.userAgent));
  }, []);

  /** 动态加载钉钉 JSAPI 脚本（只加载一次） */
  const ensureJsapi = () => {
    if (!jsapiReadyRef.current) {
      jsapiReadyRef.current = new Promise<void>((resolve, reject) => {
        if (window.dd) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = DINGTALK_JSAPI_URL;
        script.onload = () => resolve();
        script.onerror = () => {
          jsapiReadyRef.current = null;
          reject(new Error('钉钉 JSAPI 加载失败，请检查网络'));
        };
        document.head.appendChild(script);
      });
    }
    return jsapiReadyRef.current;
  };

  /** 获取免登授权码 */
  const handleGetCode = async () => {
    setCode('');
    setCodeError('');
    setResult('');
    if (!corpId.trim()) {
      setCodeError('请先填写 CorpId');
      return;
    }
    localStorage.setItem(CORP_ID_STORAGE_KEY, corpId.trim());
    try {
      await ensureJsapi();
      const dd = window.dd;
      dd.ready(() => {
        dd.runtime.permission.requestAuthCode({
          corpId: corpId.trim(),
          onSuccess: (info: { code: string }) => setCode(info.code),
          onFail: (err: unknown) => setCodeError('获取失败：' + JSON.stringify(err)),
        });
      });
      dd.error((err: unknown) => setCodeError('JSAPI 初始化失败：' + JSON.stringify(err)));
    } catch (e) {
      setCodeError((e as Error).message);
    }
  };

  /** 调用后端免登接口 */
  const handleLogin = async () => {
    if (!code) {
      return;
    }
    setLoading(true);
    setResult('');
    try {
      const resp = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const text = await resp.text();
      try {
        setResult(`HTTP ${resp.status}\n` + JSON.stringify(JSON.parse(text), null, 2));
      } catch {
        setResult(`HTTP ${resp.status}\n` + text);
      }
    } catch (e) {
      setResult('请求异常：' + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  /** 复制免登码到剪贴板（clipboard 不可用时降级到 execCommand） */
  const handleCopyCode = async () => {
    if (!code) {
      return;
    }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>钉钉免登调试</h1>
      {!inDingTalk && (
        <div style={{ ...styles.tip, ...styles.error }}>
          ⚠️ 当前不在钉钉容器内，获取免登码会失败，请在钉钉工作台中打开本页
        </div>
      )}

      <label style={styles.label}>CorpId（钉钉开发者后台-企业信息）</label>
      <input
        style={styles.input}
        value={corpId}
        onChange={(e) => setCorpId(e.target.value)}
        placeholder="ding 开头的企业 CorpId"
      />

      <label style={styles.label}>登录接口地址</label>
      <input
        style={styles.input}
        value={loginUrl}
        onChange={(e) => setLoginUrl(e.target.value)}
      />

      <div>
        <button style={styles.button} onClick={handleGetCode}>
          1. 获取免登码
        </button>
        <button
          style={{ ...styles.button, ...(code && !loading ? {} : styles.buttonDisabled) }}
          disabled={!code || loading}
          onClick={handleLogin}
        >
          2. 调用登录接口
        </button>
      </div>

      {codeError && <div style={{ ...styles.codeBox, ...styles.error }}>{codeError}</div>}
      {code && (
        <div style={styles.codeBox}>
          <b>code：</b>{code}
          <button
            style={{ ...styles.button, marginTop: 8, padding: '4px 12px', fontSize: 12, background: copied ? '#52c41a' : '#1677ff' }}
            onClick={handleCopyCode}
          >
            {copied ? '已复制' : '复制 code'}
          </button>
          <div style={styles.tip}>5 分钟有效且仅可使用一次，调用失败请重新获取</div>
        </div>
      )}
      {result && <pre style={styles.resultBox}>{result}</pre>}
    </div>
  );
}
